import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Box from '@nubank/nuds-web/components/Box/Box';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Button from '@nubank/nuds-web/components/Button/Button';

import {
  getBaseRates,
  baseRatesPropTypes,
  calculateBalance as calculateBalanceFn,
} from 'services/rates';

import tracking from '../../../../tracking';

import { InputWrapper } from './styles/InputWrapper';

const INITIAL_CALCULATOR_VALUES = {
  initialBalance: 800,
  monthlyDeposits: 200,
  periodInYears: 3,
};

function AccountCalculator({ onBalanceChange, baseRates, calculateBalance }) {
  const { formatMessage } = useIntl();
  const [calculator, setCalculator] = useState(INITIAL_CALCULATOR_VALUES);

  useEffect(() => {
    const { initialBalance, monthlyDeposits, periodInYears } = calculator;
    const periodInMonths = periodInYears * 12;
    const accumulator = initialBalance + monthlyDeposits * periodInMonths;
    const { liquidBalance, finalNuconta: rawBalance } = calculateBalance(baseRates,
      { initialBalance, monthlyDeposits, period: periodInMonths });
    onBalanceChange({
      accumulator, liquidBalance, rawBalance, periodInYears,
    });
  }, [calculator]);

  const handleInitialBalanceChange = interval => event => {
    event.preventDefault();
    const value = interval
      ? (calculator.initialBalance + interval)
      : Number.parseInt(event.target.value, 10);
    const initialBalance = value >= 0 ? value : 0;
    setCalculator({ ...calculator, initialBalance });
    tracking.calculator();
  };

  const handleMonthlyDepositsChange = interval => event => {
    event.preventDefault();
    const value = interval
      ? (calculator.monthlyDeposits + interval)
      : Number.parseInt(event.target.value, 10);
    const monthlyDeposits = value >= 0 ? value : 0;
    setCalculator({ ...calculator, monthlyDeposits });
    tracking.calculator();
  };

  const handlePeriodInYearsChange = interval => event => {
    event.preventDefault();
    const periodInYears = interval
      ? (calculator.periodInYears + interval)
      : Number.parseInt(event.target.value || 0, 10);
    if (periodInYears <= 0) {
      setCalculator({ ...calculator, periodInYears: 0 });
    } else if (periodInYears >= 99) {
      setCalculator({ ...calculator, periodInYears: 99 });
    } else {
      setCalculator({ ...calculator, periodInYears });
    }
    tracking.calculator();
  };

  return (
    <>
      <Box display="grid" gridRowGap={{ xs: '1x', md: '4x' }} marginBottom="6x">
        <Typography
          tag="label"
          htmlFor="calculator-initial-balance"
          variant="subtitle"
          intlKey="ACCOUNT.CALCULATOR.INITIAL_DEPOSIT.LABEL"
        />
        <Box display="flex">
          <InputWrapper tag="div" variant="title">
            <span>R$</span>
            <input
              value={calculator.initialBalance}
              id="calculator-initial-balance"
              onChange={handleInitialBalanceChange()}
            />
          </InputWrapper>
          <Box
            display="grid"
            gridTemplateColumns="repeat(2,auto)"
            alignItems="center"
            gridColumnGap={{
              xs: '4x', md: '8x', lg: '4x', xl: '8x',
            }}
          >
            <Button
              variant="contained"
              styleVariant="primary"
              iconProps={{ name: 'minus' }}
              aria-label={formatMessage({ id: 'ACCOUNT.CALCULATOR.INITIAL_DEPOSIT.MINUS_BUTTON.ARIA_ROLE' })}
              disabled={calculator.initialBalance <= 0}
              onClick={handleInitialBalanceChange(-100)}
            />
            <Button
              variant="contained"
              styleVariant="primary"
              iconProps={{ name: 'plus' }}
              aria-label={formatMessage({ id: 'ACCOUNT.CALCULATOR.INITIAL_DEPOSIT.PLUS_BUTTON.ARIA_ROLE' })}
              onClick={handleInitialBalanceChange(100)}
            />
          </Box>
        </Box>
      </Box>

      <Box display="grid" gridRowGap={{ xs: '1x', md: '4x' }} marginBottom="6x">
        <Typography
          tag="label"
          htmlFor="calculator-monthly-deposits"
          variant="subtitle"
          intlKey="ACCOUNT.CALCULATOR.MONTHLY_DEPOSIT.LABEL"
        />
        <Box display="flex">
          <InputWrapper tag="div" variant="title">
            <span>R$</span>
            <input
              value={calculator.monthlyDeposits}
              id="calculator-monthly-deposits"
              onChange={handleMonthlyDepositsChange()}
            />
          </InputWrapper>
          <Box
            display="grid"
            gridTemplateColumns="repeat(2,auto)"
            alignItems="center"
            gridColumnGap={{
              xs: '4x', md: '8x', lg: '4x', xl: '8x',
            }}
          >
            <Button
              variant="contained"
              styleVariant="primary"
              iconProps={{ name: 'minus' }}
              aria-label={formatMessage({ id: 'ACCOUNT.CALCULATOR.MONTHLY_DEPOSIT.MINUS_BUTTON.ARIA_ROLE' })}
              disabled={calculator.monthlyDeposits <= 0}
              onClick={handleMonthlyDepositsChange(-100)}
            />
            <Button
              variant="contained"
              styleVariant="primary"
              iconProps={{ name: 'plus' }}
              aria-label={formatMessage({ id: 'ACCOUNT.CALCULATOR.MONTHLY_DEPOSIT.PLUS_BUTTON.ARIA_ROLE' })}
              onClick={handleMonthlyDepositsChange(100)}
            />
          </Box>
        </Box>
      </Box>

      <Box display="grid" gridRowGap={{ xs: '1x', md: '4x' }}>
        <Typography
          tag="label"
          htmlFor="calculator-period-in-years"
          variant="subtitle"
          intlKey="ACCOUNT.CALCULATOR.PERIOD_IN_YEARS.LABEL"
        />
        <Box display="flex">
          <InputWrapper tag="div" variant="title" inputSize={calculator.periodInYears.toString().length}>
            <input
              value={calculator.periodInYears}
              id="calculator-period-in-years"
              onChange={handlePeriodInYearsChange()}
            />
            <span>{formatMessage({ id: 'ACCOUNT.CALCULATOR.PERIOD_IN_YEARS.SUFFIX' })}</span>
          </InputWrapper>
          <Box
            display="grid"
            gridTemplateColumns="repeat(2,auto)"
            alignItems="center"
            gridColumnGap={{
              xs: '4x', md: '8x', lg: '4x', xl: '8x',
            }}
          >
            <Button
              variant="contained"
              styleVariant="primary"
              iconProps={{ name: 'minus' }}
              aria-label={formatMessage({ id: 'ACCOUNT.CALCULATOR.PERIOD_IN_YEARS.MINUS_BUTTON.ARIA_ROLE' })}
              disabled={calculator.periodInYears <= 0}
              onClick={handlePeriodInYearsChange(-1)}
            />
            <Button
              variant="contained"
              styleVariant="primary"
              iconProps={{ name: 'plus' }}
              aria-label={formatMessage({ id: 'ACCOUNT.CALCULATOR.PERIOD_IN_YEARS.PLUS_BUTTON.ARIA_ROLE' })}
              disabled={calculator.periodInYears >= 99}
              onClick={handlePeriodInYearsChange(1)}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}

AccountCalculator.propTypes = {
  baseRates: baseRatesPropTypes,
  calculateBalance: PropTypes.func,
  onBalanceChange: PropTypes.func,
};

AccountCalculator.defaultProps = {
  calculateBalance: calculateBalanceFn,
  onBalanceChange: () => { },
  baseRates: getBaseRates(),
};

export default React.memo(AccountCalculator);
